import { apiClient } from './apiClient'
import { environment } from '../../config/environment'
import type { Continent, Country } from '../../types/continent'
import { logger } from '../../utils/logger'

export class ContinentsService {
  /**
   * Get all continents with their countries
   */
  static async getContinents(): Promise<Continent[]> {
    const mode = environment.getConfig().mode
    
    if (mode === 'fake') {
      return await ContinentsService.getMockContinents()
    }
    
    logger.log('📦 Fetching continents from API...')
    const response = await apiClient.get<Continent[]>('/v1/continents')
    logger.log('✅ Continents received:', response)
    
    return response || []
  }

  /**
   * Get a single continent by ID
   */
  static async getContinent(id: number): Promise<Continent | null> {
    const mode = environment.getConfig().mode
    
    if (mode === 'fake') {
      return await ContinentsService.getMockContinent(id)
    }
    
    try {
      const continent = await apiClient.get<Continent>(`/v1/continents/${id}`)
      return continent
    } catch (error) {
      // Return null if continent not found
      return null
    }
  }

  /**
   * Create a new continent
   */
  static async createContinent(continent: Omit<Continent, 'id'>): Promise<Continent> {
    const createdContinent = await apiClient.post<Continent>('/v1/continents', continent)
    return createdContinent
  }

  /**
   * Update an existing continent
   */
  static async updateContinent(continent: Continent): Promise<Continent> {
    if (!continent.id) {
      throw new Error('Continent ID is required for update')
    }
    const updatedContinent = await apiClient.put<Continent>(`/v1/continents/${continent.id}`, continent)
    return updatedContinent
  }

  /**
   * Delete a continent
   */
  static async deleteContinent(id: number): Promise<void> {
    await apiClient.delete(`/v1/continents/${id}`)
  }

  /**
   * Add a country to a continent
   */
  static async addCountry(continentId: number, country: Omit<Country, 'id'>): Promise<Country> {
    const response = await apiClient.post<Country>(`/v1/continents/${continentId}/countries`, country)
    return response
  }

  /**
   * Update a country in a continent
   */
  static async updateCountry(continentId: number, country: Country): Promise<void> {
    if (!country.id) {
      throw new Error('Country ID is required for update')
    }
    await apiClient.put(`/v1/continents/${continentId}/countries/${country.id}`, country)
  }

  /**
   * Delete a country from a continent
   */
  static async deleteCountry(continentId: number, countryId: number): Promise<void> {
    await apiClient.delete(`/v1/continents/${continentId}/countries/${countryId}`)
  }

  /**
   * Mock data for development/testing
   */
  private static async getMockContinents(): Promise<Continent[]> {
    const mockContinents: Continent[] = [
      {
        id: 1,
        name: 'Europe',
        countries: [
          { id: 1, name: 'France' },
          { id: 2, name: 'Allemagne' },
          { id: 3, name: 'Autriche' },
          { id: 4, name: 'Belgique' },
          { id: 5, name: 'Bulgarie' },
          { id: 6, name: 'Chypre' },
          { id: 7, name: 'Croatie' },
          { id: 8, name: 'Danemark' },
          { id: 9, name: 'Espagne' },
          { id: 10, name: 'Estonie' },
          { id: 11, name: 'Finlande' },
          { id: 12, name: 'Grèce' },
          { id: 13, name: 'Hongrie' },
          { id: 14, name: 'Irlande' },
          { id: 15, name: 'Italie' },
          { id: 16, name: 'Lettonie' },
          { id: 17, name: 'Lituanie' },
          { id: 18, name: 'Luxembourg' },
          { id: 19, name: 'Malte' },
          { id: 20, name: 'Pays-Bas' },
          { id: 21, name: 'Pologne' },
          { id: 22, name: 'Portugal' },
          { id: 23, name: 'République Tchèque' },
          { id: 24, name: 'Roumanie' },
          { id: 25, name: 'Slovaquie' },
          { id: 26, name: 'Slovénie' },
          { id: 27, name: 'Suède' },
          { id: 28, name: 'Suisse' },
          { id: 29, name: 'Ecosse' },
          { id: 30, name: 'Pays de Galles' },
          { id: 31, name: 'Ukraine' },
          { id: 32, name: 'Norvège' },
        ]
      },
      {
        id: 2,
        name: 'Asie',
        countries: [
          { id: 33, name: 'Cambodge' },
          { id: 34, name: 'Chine' },
          { id: 35, name: 'Corée du Sud' },
          { id: 36, name: 'Inde' },
          { id: 37, name: 'Indonésie' },
          { id: 38, name: 'Japon' },
          { id: 39, name: 'Laos' },
          { id: 40, name: 'Népal' },
          { id: 41, name: 'Philippines' },
          { id: 42, name: 'Singapour' },
          { id: 43, name: 'Sri Lanka' },
          { id: 44, name: 'Thaïlande' },
          { id: 45, name: 'Vietnam' },
        ]
      },
      {
        id: 3,
        name: 'Moyen-Orient',
        countries: [
          { id: 46, name: 'Afghanistan' },
          { id: 47, name: 'Arabie saoudite' },
          { id: 48, name: 'Bahreïn' },
          { id: 49, name: 'Egypte' },
          { id: 50, name: 'Émirats arabes unis' },
          { id: 51, name: 'Irak' },
          { id: 52, name: 'Iran' },
          { id: 53, name: 'Israël' },
          { id: 54, name: 'Jordanie' },
          { id: 55, name: 'Koweït' },
          { id: 56, name: 'Liban' },
          { id: 57, name: 'Oman' },
          { id: 58, name: 'Pakistan' },
          { id: 59, name: 'Qatar' },
          { id: 60, name: 'Syrie' },
          { id: 61, name: 'Turquie' },
          { id: 62, name: 'Yémen' },
        ]
      },
      {
        id: 4,
        name: 'Amérique du Nord',
        countries: [
          { id: 63, name: 'Canada' },
          { id: 64, name: 'États-Unis' },
        ]
      },
      {
        id: 5,
        name: 'Afrique',
        countries: [
          { id: 65, name: 'Afrique du Sud' },
          { id: 66, name: 'Algérie' },
          { id: 67, name: 'Cameroun' },
          { id: 68, name: "Côte d'Ivoire" },
          { id: 69, name: 'Gabon' },
          { id: 70, name: 'Gambie' },
          { id: 71, name: 'Ghana' },
          { id: 72, name: 'Guinée' },
          { id: 73, name: 'Guinée-Bissau' },
          { id: 74, name: 'Kenya' },
          { id: 75, name: 'Madagascar' },
          { id: 76, name: 'Mali' },
          { id: 77, name: 'Maroc' },
          { id: 78, name: 'Ile Maurice' },
          { id: 79, name: 'Mauritanie' },
          { id: 80, name: 'Mozambique' },
          { id: 81, name: 'Namibie' },
          { id: 82, name: 'Niger' },
          { id: 83, name: 'Nigeria' },
          { id: 84, name: 'République centrafricaine' },
          { id: 85, name: 'RDC' },
          { id: 86, name: 'République du Congo' },
          { id: 87, name: 'Rwanda' },
          { id: 88, name: 'Sénégal' },
          { id: 89, name: 'Sierra Leone' },
          { id: 90, name: 'Tanzanie' },
          { id: 91, name: 'Tchad' },
          { id: 92, name: 'Togo' },
          { id: 93, name: 'Tunisie' },
        ]
      },
      {
        id: 6,
        name: 'Océanie',
        countries: [
          { id: 94, name: 'Australie' },
          { id: 95, name: 'Nouvelle Zélande' },
        ]
      },
      {
        id: 7,
        name: 'Amérique du Sud',
        countries: [
          { id: 96, name: 'Argentine' },
          { id: 97, name: 'Bolivie' },
          { id: 98, name: 'Brésil' },
          { id: 99, name: 'Chili' },
          { id: 100, name: 'Colombie' },
          { id: 101, name: 'Équateur' },
          { id: 102, name: 'Mexique' },
          { id: 103, name: 'Paraguay' },
          { id: 104, name: 'Pérou' },
          { id: 105, name: 'Uruguay' },
          { id: 106, name: 'Venezuela' },
        ]
      }
    ]
    
    return new Promise((resolve) => {
      setTimeout(() => resolve(mockContinents), 300)
    })
  }

  private static async getMockContinent(id: number): Promise<Continent | null> {
    const continents = await ContinentsService.getMockContinents()
    return continents.find(c => c.id === id) || null
  }
}